import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { despachoFiscalInfoApi, parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";
import {
  csfSyncAppliedCount,
  obligationAuthorityChipClass,
  obligationAuthorityLabel,
} from "@/modules/despacho/despachoFiscalInfoHelpers";
import {
  buildAssignmentMap,
  DEACTIVATION_REASONS,
  filterCatalogItems,
  isObligationRowActive,
  OBLIGATION_AUTHORITY_FILTERS,
  obligationVisibleName,
} from "@/modules/despacho/obligationCatalogHelpers";

function CollapsibleBlock({ title, description, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={testId}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-sm border border-slate-200 bg-slate-50/80 px-3 py-2 text-left">
        <div>
          <p className="text-sm font-bold text-[#0F2D5C] m-0">{title}</p>
          {description ? <p className="text-[11px] text-slate-500 m-0 mt-0.5">{description}</p> : null}
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3 space-y-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function AssignedRow({ row, readOnly, busy, onDeactivate, onReactivate, variant = "default" }) {
  const label = row.nombre_visible || obligationVisibleName(row.catalog) || row.matched_label || "Obligación";
  const active = isObligationRowActive(row);
  const isCsf = row.source === "csf_suggested" || row.source === "csf_confirmed";
  const isCsfInferred = row.source === "csf_inferred";
  const isCfdiConfirmed = row.source === "cfdi_confirmed" || row.source === "cfdi_detected";

  return (
    <div className="flex items-start justify-between gap-3 rounded-sm border border-slate-200 bg-white px-3 py-2">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{label}</span>
          {row.catalog?.authority ? (
            <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border ${obligationAuthorityChipClass(row.catalog.authority)}`}>
              {obligationAuthorityLabel(row.catalog.authority)}
            </span>
          ) : null}
          {isCsf ? <Badge variant="secondary" className="rounded-sm text-[10px]">Activa desde CFS</Badge> : null}
          {isCsfInferred ? (
            <Badge variant="secondary" className="rounded-sm text-[10px] bg-violet-50 text-violet-900 border-violet-200">
              {row.inference_kind === "cont_elect_pm_regimen"
                ? "Inferida (reglas SAT)"
                : "Inferida (consistencia CFS)"}
            </Badge>
          ) : null}
          {isCfdiConfirmed && variant !== "cfdi" ? (
            <Badge variant="secondary" className="rounded-sm text-[10px]">Desde CFDI</Badge>
          ) : null}
          {variant === "cfdi" ? <Badge variant="secondary" className="rounded-sm text-[10px]">CFDI</Badge> : null}
          {!active ? <Badge variant="secondary" className="rounded-sm text-[10px] bg-slate-100">Inactiva</Badge> : null}
        </div>
        {row.csf_text ? <p className="text-[10px] text-slate-500 m-0 truncate">CFS: {row.csf_text}</p> : null}
        {row.detection_reason ? <p className="text-[10px] text-slate-500 m-0">{row.detection_reason}</p> : null}
      </div>
      {!readOnly ? (
        active ? (
          <Button type="button" variant="outline" size="sm" className="rounded-sm h-7 shrink-0" disabled={busy} onClick={() => onDeactivate(row)}>
            Desactivar
          </Button>
        ) : (
          <Button type="button" size="sm" className="rounded-sm h-7 shrink-0 bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={busy} onClick={() => onReactivate(row)}>
            Reactivar
          </Button>
        )
      ) : null}
    </div>
  );
}

function CsfDetectedItem({
  item,
  readOnly,
  busyId,
  onDeactivate,
  onReactivate,
  onActivate,
}) {
  const assignedRow = item?.row;
  if (assignedRow) {
    return (
      <AssignedRow
        row={assignedRow}
        readOnly={readOnly}
        busy={busyId === assignedRow.id}
        onDeactivate={onDeactivate}
        onReactivate={onReactivate}
      />
    );
  }

  const catalogId = item?.obligation_catalog_id;
  const label = item?.nombre_visible || item?.csf_text || "Obligación";
  const isUnmatched = Boolean(item?.unmatched) || !catalogId;

  return (
    <div className={`flex items-start justify-between gap-3 rounded-sm border px-3 py-2 ${isUnmatched ? "border-dashed border-amber-300 bg-amber-50/40" : "border-dashed border-slate-300 bg-slate-50/50"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-slate-900 m-0">{label}</p>
          {item?.catalog?.authority ? (
            <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border ${obligationAuthorityChipClass(item.catalog.authority)}`}>
              {obligationAuthorityLabel(item.catalog.authority)}
            </span>
          ) : null}
          <Badge variant="secondary" className="rounded-sm text-[10px]">
            {isUnmatched ? "Sin coincidencia en catálogo" : "Sugerida CFS"}
          </Badge>
        </div>
        {item?.csf_text ? <p className="text-[10px] text-slate-500 m-0 mt-1 truncate">CFS: {item.csf_text}</p> : null}
      </div>
      {!readOnly && !isUnmatched ? (
        <Button
          type="button"
          size="sm"
          className="rounded-sm h-7 shrink-0 bg-[#0F2D5C] hover:bg-[#0a2248]"
          disabled={busyId === catalogId}
          onClick={() => onActivate(item)}
        >
          {busyId === catalogId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activar"}
        </Button>
      ) : null}
    </div>
  );
}

export default function FiscalObligationsPanel({
  clientId,
  detail,
  catalog,
  readOnly = false,
  onReload,
  onUpdated,
}) {
  const [authorityFilter, setAuthorityFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [busyId, setBusyId] = useState("");
  const [syncBusy, setSyncBusy] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivateNotes, setDeactivateNotes] = useState("");

  const hasCsf = Boolean(detail?.csf_meta?.has_csf);
  const cfsRows = detail?.obligations?.cfs || [];
  const cfsDetected = detail?.obligations?.cfs_detected;
  const useCfsDetected = Array.isArray(cfsDetected);
  const cfsSuggestions = detail?.obligations?.cfs_suggestions || [];
  const manualRows = detail?.obligations?.manual || [];
  const cfdiRows = detail?.obligations?.cfdi || [];
  const cfdiSuggestions = detail?.obligations?.cfdi_suggestions || [];
  const repseSuggestions = detail?.obligations?.repse_suggestions || [];
  const activeManualRows = useMemo(
    () => manualRows.filter((row) => isObligationRowActive(row)),
    [manualRows],
  );
  const activeCfsRows = useMemo(
    () => cfsRows.filter((row) => isObligationRowActive(row)),
    [cfsRows],
  );
  const activeCfdiRows = useMemo(
    () => cfdiRows.filter((row) => isObligationRowActive(row)),
    [cfdiRows],
  );
  const allAssigned = useMemo(
    () => [...cfsRows, ...manualRows, ...cfdiRows],
    [cfsRows, manualRows, cfdiRows],
  );
  const assignmentMap = useMemo(() => buildAssignmentMap(allAssigned), [allAssigned]);
  const visibleCfsSuggestions = useMemo(
    () => (cfsSuggestions || []).filter((sug) => !assignmentMap[sug.obligation_catalog_id]),
    [cfsSuggestions, assignmentMap],
  );
  const visibleCfdiSuggestions = useMemo(
    () => (cfdiSuggestions || []).filter((sug) => !assignmentMap[sug.obligation_catalog_id]),
    [cfdiSuggestions, assignmentMap],
  );
  const visibleRepeseSuggestions = useMemo(
    () => (repseSuggestions || []).filter((sug) => !assignmentMap[sug.obligation_catalog_id]),
    [repseSuggestions, assignmentMap],
  );
  const showPayrollBundle = useMemo(
    () => visibleCfdiSuggestions.some(
      (s) => s.inference_kind === "payroll_bundle"
        || s.obligation_catalog_id === "sat_cfdi_nomina"
        || s.obligation_catalog_id === "sat_ret_sueldos",
    ) || activeCfsRows.some((row) => row.obligation_catalog_id === "sat_ret_sueldos"),
    [visibleCfdiSuggestions, activeCfsRows],
  );
  const showContElectInfo = useMemo(() => {
    const rows = [...allAssigned, ...visibleCfsSuggestions, ...visibleCfdiSuggestions];
    return rows.some((row) => row.obligation_catalog_id === "sat_cont_elect");
  }, [allAssigned, visibleCfsSuggestions, visibleCfdiSuggestions]);
  const cfsSectionOpen = useMemo(
    () => (
      (useCfsDetected && cfsDetected.length > 0)
      || cfsRows.length > 0
      || visibleCfsSuggestions.length > 0
      || Boolean(detail?.csf_meta?.has_csf)
    ),
    [cfsDetected, cfsRows.length, detail?.csf_meta?.has_csf, useCfsDetected, visibleCfsSuggestions.length],
  );

  const catalogFiltered = useMemo(
    () => filterCatalogItems(catalog, { authority: authorityFilter, query: searchQuery, assignmentMap }),
    [catalog, authorityFilter, searchQuery, assignmentMap],
  );

  const handleAssign = async (catalogItem, source = "manual_assignment", extra = {}) => {
    if (!clientId || readOnly || !catalogItem?.id) return;
    setBusyId(catalogItem.id);
    try {
      await despachoFiscalInfoApi.addClientObligation(clientId, catalogItem.id, {
        source,
        ...extra,
      });
      toast.success(`${obligationVisibleName(catalogItem)} activada.`);
      await onReload?.();
      onUpdated?.();
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      if (parsed.kind === "conflict") {
        toast.message(parsed.message || "Esta obligación ya existía; puede reactivarla.");
        await onReload?.();
      } else {
        toast.error(parsed.message);
      }
    } finally {
      setBusyId("");
    }
  };

  const handleActivateCsfSuggestion = async (sug) => {
    await handleAssign(
      { id: sug.obligation_catalog_id, nombre_visible: sug.nombre_visible, catalog: sug.catalog },
      "csf_suggested",
      { csf_text: sug.csf_text },
    );
  };

  const handleActivateCsfDetected = async (item) => {
    if (!item?.obligation_catalog_id) return;
    await handleActivateCsfSuggestion({
      obligation_catalog_id: item.obligation_catalog_id,
      nombre_visible: item.nombre_visible,
      catalog: item.catalog,
      csf_text: item.csf_text,
    });
  };

  const handleActivateCfdiSuggestion = async (sug) => {
    await handleAssign(
      { id: sug.obligation_catalog_id, nombre_visible: sug.nombre_visible },
      "cfdi_confirmed",
      {
        detection_reason: sug.detection_reason,
        detected_from_period: sug.detected_from_period,
        detected_from_cfdi_type: sug.detected_from_cfdi_type,
      },
    );
  };

  const handleActivateRepseSuggestion = async (sug) => {
    await handleAssign(
      { id: sug.obligation_catalog_id, nombre_visible: sug.nombre_visible },
      "manual_assignment",
      {
        detection_reason: sug.detection_reason,
        detected_from_cfdi_type: sug.detected_from_cfdi_type || "repse_profile",
      },
    );
  };

  const handleActivatePayrollBundle = async () => {
    if (!clientId || readOnly) return;
    setBusyId("__payroll_bundle__");
    try {
      const result = await despachoFiscalInfoApi.activatePayrollBundle(clientId);
      const count = result?.count || 0;
      if (count) {
        toast.success(`Paquete nómina activado (${count} obligación(es) en Otras obligaciones).`);
      } else {
        toast.message("No había obligaciones laborales pendientes por activar.");
      }
      await onReload?.();
      onUpdated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleCatalogAction = async (item) => {
    const prior = item.assignment?.row;
    if (item.assignment?.state === "inactive" && prior?.id) {
      await handleReactivate(prior);
      return;
    }
    await handleAssign(item);
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivateTarget?.id || !clientId) return;
    if (!deactivateReason) {
      toast.error("Seleccione un motivo de desactivación.");
      return;
    }
    setBusyId(deactivateTarget.id);
    try {
      await despachoFiscalInfoApi.patchClientObligation(clientId, deactivateTarget.id, {
        active: false,
        deactivation_reason: deactivateReason,
        deactivation_notes: deactivateNotes,
      });
      toast.success("Obligación desactivada. Disponible en el catálogo maestro para activarla.");
      setDeactivateTarget(null);
      setDeactivateReason("");
      setDeactivateNotes("");
      await onReload?.();
      onUpdated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleReactivate = async (row) => {
    if (!row?.id || !clientId) return;
    setBusyId(row.id);
    try {
      await despachoFiscalInfoApi.patchClientObligation(clientId, row.id, { active: true });
      toast.success("Obligación activada.");
      await onReload?.();
      onUpdated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleRecalcFromCsf = async () => {
    if (!clientId || readOnly || !hasCsf || syncBusy) return;
    setSyncBusy(true);
    try {
      const syncResult = await despachoFiscalInfoApi.syncCsfInternal(clientId);
      await onReload?.();
      const applied = csfSyncAppliedCount(syncResult);
      if (applied > 0) {
        toast.success(`${applied} obligación(es) actualizada(s) desde la constancia fiscal.`);
      } else {
        toast.success("Obligaciones recalculadas desde la constancia fiscal.");
      }
      onUpdated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="fiscal-obligations-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-[#0F2D5C] m-0">Obligaciones</h3>
          <p className="text-[11px] text-slate-500 m-0 mt-1">
            Detectadas en la constancia fiscal (línea directa); las inferidas por consistencia aparecen en «Otras obligaciones».
            Tras subir una CFS nueva o modificada, use «Recalcular» para actualizar el catálogo.
          </p>
          {!hasCsf ? (
            <p className="text-[11px] text-amber-800 m-0 mt-1" data-testid="fiscal-obl-recalc-csf-hint">
              No hay constancia almacenada. Súbala en la pestaña <strong>Constancia fiscal</strong> y luego recalcule.
            </p>
          ) : null}
        </div>
        {!readOnly ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm shrink-0 border-[#0F2D5C]/40 text-[#0F2D5C] hover:bg-[#0F2D5C]/[0.06]"
            disabled={syncBusy || !hasCsf}
            onClick={handleRecalcFromCsf}
            data-testid="fiscal-obl-recalc-csf"
            title={
              hasCsf
                ? "Vuelve a leer la CFS almacenada y actualiza obligaciones"
                : "Primero suba una constancia fiscal"
            }
          >
            {syncBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            )}
            Recalcular desde constancia
          </Button>
        ) : null}
      </div>

      <CollapsibleBlock
        title="A) Obligaciones desde CFS"
        description="Detectadas en la constancia fiscal."
        defaultOpen={cfsSectionOpen}
        testId="fiscal-obl-block-cfs"
      >
        {(detail?.diagnostics?.warnings || []).length ? (
          <div className="rounded-sm border border-amber-200 bg-amber-50/60 px-3 py-2 text-[10px] text-amber-950 space-y-1 mb-2">
            {(detail.diagnostics.warnings || []).map((w) => (
              <p key={w} className="m-0">{w}</p>
            ))}
          </div>
        ) : null}
        {(detail?.diagnostics?.hints || []).filter((h) => !h.includes("CFDI")).map((hint) => (
          <p key={hint} className="text-[10px] text-slate-600 m-0 mb-2">{hint}</p>
        ))}
        {(detail?.csf_meta?.obligaciones_unmatched_count || 0) > 0 ? (
          <p className="text-[10px] text-amber-800 m-0 mb-2">
            {detail.csf_meta.obligaciones_unmatched_count} renglón(es) de la constancia no coinciden con el catálogo;
            aparecen abajo con borde ámbar para asignación manual.
          </p>
        ) : null}
        {useCfsDetected ? cfsDetected.map((item, index) => (
          <CsfDetectedItem
            key={`${item.obligation_catalog_id || "raw"}-${index}`}
            item={item}
            readOnly={readOnly}
            busyId={busyId}
            onDeactivate={setDeactivateTarget}
            onReactivate={handleReactivate}
            onActivate={handleActivateCsfDetected}
          />
        )) : null}
        {!useCfsDetected && cfsRows.length ? cfsRows.map((row) => (
          <AssignedRow
            key={row.id}
            row={row}
            readOnly={readOnly}
            busy={busyId === row.id}
            onDeactivate={setDeactivateTarget}
            onReactivate={handleReactivate}
          />
        )) : null}
        {!useCfsDetected && visibleCfsSuggestions.length ? visibleCfsSuggestions.map((sug) => (
          <div key={sug.obligation_catalog_id} className="flex items-start justify-between gap-3 rounded-sm border border-dashed border-slate-300 bg-slate-50/50 px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-slate-900 m-0">{sug.nombre_visible}</p>
                {sug.catalog?.authority ? (
                  <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border ${obligationAuthorityChipClass(sug.catalog.authority)}`}>
                    {obligationAuthorityLabel(sug.catalog.authority)}
                  </span>
                ) : null}
                <Badge variant="secondary" className="rounded-sm text-[10px]">Sugerida CFS</Badge>
              </div>
              {sug.csf_text ? <p className="text-[10px] text-slate-500 m-0 mt-1 truncate">CFS: {sug.csf_text}</p> : null}
            </div>
            {!readOnly ? (
              <Button
                type="button"
                size="sm"
                className="rounded-sm h-7 shrink-0 bg-[#0F2D5C] hover:bg-[#0a2248]"
                disabled={busyId === sug.obligation_catalog_id}
                onClick={() => handleActivateCsfSuggestion(sug)}
              >
                {busyId === sug.obligation_catalog_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activar"}
              </Button>
            ) : null}
          </div>
        )) : null}
        {useCfsDetected && !cfsDetected.length ? (
          <p className="text-xs text-slate-500 m-0">
            {detail?.csf_meta?.has_csf
              ? "No se detectaron obligaciones en la constancia. Use «Sincronizar CFS interno» o active del catálogo maestro."
              : "Sin obligaciones desde CFS. Suba la constancia fiscal para detectarlas."}
          </p>
        ) : null}
        {!useCfsDetected && !cfsRows.length && !visibleCfsSuggestions.length ? (
          <p className="text-xs text-slate-500 m-0">
            {detail?.csf_meta?.has_csf
              ? "Sin obligaciones activas desde CFS. Use «Sincronizar CFS interno» arriba o active del catálogo maestro."
              : "Sin obligaciones desde CFS. Suba la constancia fiscal para detectarlas."}
          </p>
        ) : null}
      </CollapsibleBlock>

      <CollapsibleBlock
        title="B) Otras obligaciones"
        description="Manuales o inferidas por consistencia con impuestos detectados en la constancia (IMSS, INFONAVIT, ISN, contabilidad electrónica, etc.)."
        testId="fiscal-obl-block-manual"
      >
        {showContElectInfo ? (
          <div
            className="rounded-sm border border-slate-200 bg-slate-50/80 px-3 py-2 text-[10px] text-slate-600 mb-2"
            data-testid="fiscal-obl-cont-elect-info"
          >
            <p className="m-0 font-medium text-slate-800">Contabilidad electrónica</p>
            <p className="m-0 mt-1">
              Esta obligación no suele aparecer en la opinión de cumplimiento ni como línea explícita en la CFS.
              Se infiere por régimen (personas morales generales) o por ingresos cobrados ≥ $4,000,000 anuales
              en personas físicas con actividad empresarial, profesional o arrendamiento (excluye RESICO, RIF y regímenes de menores ingresos).
            </p>
          </div>
        ) : null}
        {activeManualRows.length ? activeManualRows.map((row) => (
          <AssignedRow
            key={row.id}
            row={row}
            readOnly={readOnly}
            busy={busyId === row.id}
            onDeactivate={setDeactivateTarget}
            onReactivate={handleReactivate}
          />
        )) : (
          <p className="text-xs text-slate-500 m-0">No hay obligaciones manuales asignadas.</p>
        )}
      </CollapsibleBlock>

      <CollapsibleBlock
        title="C) Sugeridas por CFDI"
        description="Basadas en XML ya almacenados. Al activar pasan a «Otras obligaciones»."
        testId="fiscal-obl-block-cfdi"
      >
        {!readOnly && showPayrollBundle ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-emerald-200 bg-emerald-50/60 px-3 py-2">
            <p className="text-xs text-emerald-950 m-0">
              Se detectó nómina. Puede activar IMSS, INFONAVIT bimestral e ISN que no estén ya en la constancia.
            </p>
            <Button
              type="button"
              size="sm"
              className="rounded-sm h-7 shrink-0 bg-[#0F2D5C] hover:bg-[#0a2248]"
              disabled={busyId === "__payroll_bundle__"}
              onClick={handleActivatePayrollBundle}
              data-testid="fiscal-obl-activate-payroll-bundle"
            >
              {busyId === "__payroll_bundle__" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activar paquete nómina"}
            </Button>
          </div>
        ) : null}
        {activeCfdiRows.map((row) => (
          <AssignedRow
            key={row.id}
            row={row}
            variant="cfdi"
            readOnly={readOnly}
            busy={busyId === row.id}
            onDeactivate={setDeactivateTarget}
            onReactivate={handleReactivate}
          />
        ))}
        {visibleCfdiSuggestions.length ? visibleCfdiSuggestions.map((sug) => (
          <div key={sug.obligation_catalog_id} className="flex items-start justify-between gap-3 rounded-sm border border-dashed border-slate-300 bg-slate-50/50 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 m-0">{sug.nombre_visible}</p>
              <p className="text-[10px] text-slate-500 m-0 mt-1">{sug.detection_reason}</p>
            </div>
            {!readOnly ? (
              <Button
                type="button"
                size="sm"
                className="rounded-sm h-7 shrink-0 bg-[#0F2D5C] hover:bg-[#0a2248]"
                disabled={busyId === sug.obligation_catalog_id}
                onClick={() => handleActivateCfdiSuggestion(sug)}
              >
                {busyId === sug.obligation_catalog_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activar"}
              </Button>
            ) : null}
          </div>
        )) : null}
        {!activeCfdiRows.length && !visibleCfdiSuggestions.length ? (
          <p className="text-xs text-slate-500 m-0">
            {detail?.cfdi_meta?.docs_scanned > 0
              ? `Se analizaron ${detail.cfdi_meta.docs_scanned} CFDI almacenado(s); no hay sugerencias pendientes (retenciones/nómina ya activas o no aplican).`
              : "Sin sugerencias por CFDI en este cliente. Cargue XML en Mis CFDI; esta sección no usa la constancia fiscal."}
          </p>
        ) : null}
        {(detail?.diagnostics?.hints || []).map((hint) => (
          hint.includes("CFDI") ? (
            <p key={hint} className="text-[10px] text-slate-500 m-0 mt-1">{hint}</p>
          ) : null
        ))}
        {(detail?.diagnostics?.warnings || []).length ? (
          <div className="rounded-sm border border-amber-200 bg-amber-50/60 px-3 py-2 text-[10px] text-amber-950 space-y-1">
            {(detail.diagnostics.warnings || []).map((w) => (
              <p key={w} className="m-0">{w}</p>
            ))}
          </div>
        ) : null}
      </CollapsibleBlock>

      {visibleRepeseSuggestions.length ? (
        <CollapsibleBlock
          title="D) Sugeridas por REPSE"
          description="Cuatrimestrales vinculadas al registro REPSE del cliente."
          testId="fiscal-obl-block-repse"
        >
          {visibleRepeseSuggestions.map((sug) => (
            <div key={sug.obligation_catalog_id} className="flex items-start justify-between gap-3 rounded-sm border border-dashed border-violet-300 bg-violet-50/40 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 m-0">{sug.nombre_visible}</p>
                <p className="text-[10px] text-slate-500 m-0 mt-1">{sug.detection_reason}</p>
              </div>
              {!readOnly ? (
                <Button
                  type="button"
                  size="sm"
                  className="rounded-sm h-7 shrink-0 bg-[#0F2D5C] hover:bg-[#0a2248]"
                  disabled={busyId === sug.obligation_catalog_id}
                  onClick={() => handleActivateRepseSuggestion(sug)}
                >
                  {busyId === sug.obligation_catalog_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activar"}
                </Button>
              ) : null}
            </div>
          ))}
        </CollapsibleBlock>
      ) : null}

      <CollapsibleBlock
        title="Catálogo maestro"
        description="Busque y active obligaciones para esta empresa."
        defaultOpen={false}
        testId="fiscal-obl-block-catalog"
      >
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" aria-hidden />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar obligación…"
              className="pl-8 rounded-sm h-9 text-sm"
              data-testid="fiscal-catalog-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {OBLIGATION_AUTHORITY_FILTERS.map((f) => (
              <Button
                key={f.key}
                type="button"
                size="sm"
                variant={authorityFilter === f.key ? "default" : "outline"}
                className={`rounded-sm h-7 text-[11px] ${authorityFilter === f.key ? "bg-[#0F2D5C] hover:bg-[#0a2248]" : ""}`}
                onClick={() => setAuthorityFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
            {catalogFiltered.map((item) => {
              const state = item.assignment?.state || "unassigned";
              const label = obligationVisibleName(item);
              const priorRow = item.assignment?.row;
              const busyKey = state === "inactive" && priorRow?.id ? priorRow.id : item.id;
              return (
                <div key={item.id} className="flex items-center justify-between gap-2 rounded-sm border border-slate-200 px-2.5 py-2 bg-white">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-900 m-0 truncate">{label}</p>
                    <p className="text-[10px] text-slate-500 m-0">{item.categoria || item.periodicidad || item.periodicity}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!readOnly ? (
                      <Button
                        type="button"
                        size="sm"
                        className="rounded-sm h-7 bg-[#0F2D5C] hover:bg-[#0a2248]"
                        disabled={busyId === busyKey}
                        onClick={() => handleCatalogAction(item)}
                      >
                        {busyId === busyKey ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activar"}
                      </Button>
                    ) : state === "inactive" ? (
                      <Badge variant="secondary" className="rounded-sm text-[10px]">Inactiva</Badge>
                    ) : (
                      <span className="text-[10px] text-slate-400">No asignada</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CollapsibleBlock>

      <Dialog open={Boolean(deactivateTarget)} onOpenChange={(open) => !open && setDeactivateTarget(null)}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Desactivar obligación</DialogTitle>
            <DialogDescription>
              {deactivateTarget?.source?.startsWith("csf")
                ? "Esta obligación proviene de la constancia fiscal. Si la desactiva, quedará registrada la modificación manual con motivo."
                : "La obligación dejará de aplicar y volverá al catálogo maestro para reactivarla cuando la necesite."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Motivo</Label>
              <Select value={deactivateReason} onValueChange={setDeactivateReason}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleccione motivo…" /></SelectTrigger>
                <SelectContent>
                  {DEACTIVATION_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Comentario (opcional)</Label>
              <Input value={deactivateNotes} onChange={(e) => setDeactivateNotes(e.target.value)} className="rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setDeactivateTarget(null)} disabled={Boolean(busyId)}>Cancelar</Button>
            <Button
              type="button"
              className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
              onClick={handleDeactivateConfirm}
              disabled={!deactivateReason || Boolean(busyId)}
            >
              {busyId === deactivateTarget?.id ? "Desactivando…" : "Desactivar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
